import React, { useEffect, useMemo, useState } from 'react';
import { Link as LinkIcon } from 'lucide-react';
import Modal from './Modal';
import { dicomHelpers } from '../services/dicomHelpers';
import {
  EXPIRY_CUSTOM,
  EXPIRY_NEVER,
  EXPIRY_PRESETS,
  buildShareLink,
  copyToClipboard,
  presetToExpiresAt,
  toDateTimeLocalValue,
} from '../services/imagingShares';
import './ImagingCheckbox.css';
import './ShareEditorModal.css';

function initialPresetFor(expiresAt) {
  if (!expiresAt) return EXPIRY_NEVER;
  return EXPIRY_CUSTOM;
}

function ShareEditorModal({
  isOpen,
  onClose,
  mode = 'create',
  studies = [],
  initialSelectedUIDs = [],
  initialExpiresAt = null,
  onSubmit,
  onCreated,
}) {
  const [selected, setSelected] = useState(() => new Set(initialSelectedUIDs));
  const [preset, setPreset] = useState(() => initialPresetFor(initialExpiresAt));
  const [customDate, setCustomDate] = useState(() => toDateTimeLocalValue(initialExpiresAt));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set(initialSelectedUIDs));
    setPreset(initialPresetFor(initialExpiresAt));
    setCustomDate(toDateTimeLocalValue(initialExpiresAt));
    setSaving(false);
    setError('');
  }, [isOpen]);

  const orderedStudies = useMemo(() => {
    const initial = new Set(initialSelectedUIDs);
    return [...studies].sort((a, b) => {
      const aSel = initial.has(a.studyInstanceUID) ? 0 : 1;
      const bSel = initial.has(b.studyInstanceUID) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return String(b.studyDate || '').localeCompare(String(a.studyDate || ''));
    });
  }, [studies, initialSelectedUIDs]);

  const toggleStudy = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const expiresAt = presetToExpiresAt(preset, customDate);
  const customInvalid = preset === EXPIRY_CUSTOM
    && (!customDate || !expiresAt || new Date(expiresAt).getTime() <= Date.now());
  const canSubmit = selected.size > 0 && !customInvalid && !saving;
  const showStudyPicker = mode === 'edit';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const share = await onSubmit({
        studyInstanceUIDs: Array.from(selected),
        expiresAt,
      });
      if (mode === 'create' && share?.token) {
        const copied = await copyToClipboard(buildShareLink(share.token));
        onCreated?.({ share, copied });
      }
      onClose();
    } catch (err) {
      console.log('ShareEditorModal submit error:', err);
      setError(err?.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'create' ? 'Create Share' : 'Edit Share';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} contentClassName="share-editor-modal">
      <form className="share-editor-form" onSubmit={handleSubmit}>
        {showStudyPicker && (
          <div className="share-editor-section">
            <div className="share-editor-section-header">
              <span className="share-editor-section-title">Studies</span>
              <span className="share-editor-section-meta">
                {selected.size} of {studies.length} selected
              </span>
            </div>
            {orderedStudies.length === 0 ? (
              <div className="share-editor-empty">You have no saved studies to share.</div>
            ) : (
              <ul className="share-editor-study-list">
                {orderedStudies.map((study) => {
                  const uid = study.studyInstanceUID;
                  const checked = selected.has(uid);
                  const seriesCount = (study.series || []).length;
                  return (
                    <li key={uid}>
                      <label className={`share-editor-study${checked ? ' is-checked' : ''}`}>
                        <input
                          type="checkbox"
                          className="imaging-checkbox"
                          checked={checked}
                          onChange={() => toggleStudy(uid)}
                        />
                        <span className="share-editor-study-body">
                          <span className="share-editor-study-title">
                            {study.description || 'Untitled study'}
                          </span>
                          <span className="share-editor-study-meta">
                            {dicomHelpers.displayDate(study.studyDate)}
                            {seriesCount > 0 ? ` · ${seriesCount} series` : ''}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="share-editor-section">
          <div className="share-editor-section-header">
            <label className="share-editor-section-title" htmlFor="share-expiry-select">
              Link expiration
            </label>
          </div>
          <select
            id="share-expiry-select"
            className="share-editor-select"
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
          >
            {EXPIRY_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {preset === EXPIRY_CUSTOM && (
            <input
              type="datetime-local"
              className="share-editor-datetime"
              value={customDate}
              min={toDateTimeLocalValue(new Date().toISOString())}
              onChange={(e) => setCustomDate(e.target.value)}
              aria-label="Expiration date and time"
            />
          )}
          {customInvalid && customDate && (
            <div className="share-editor-error">Choose a date and time in the future.</div>
          )}
        </div>

        {error && <div className="share-editor-error">{error}</div>}

        <div className="share-editor-actions">
          <button type="button" className="imaging-action-button outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="imaging-action-button primary" disabled={!canSubmit}>
            <LinkIcon size={18} strokeWidth={2} />
            <span>
              {saving
                ? (mode === 'create' ? 'Creating…' : 'Saving…')
                : (mode === 'create' ? 'Create & Copy Link' : 'Save Changes')}
            </span>
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ShareEditorModal;
